import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { SlidersHorizontal, X, Lock } from "lucide-react";
import { PaywallToggle } from "./PaywallToggle";

type TuneCtx = {
  open: boolean;
  setOpen: (v: boolean) => void;
  target: HTMLElement | null;
};

const Ctx = createContext<TuneCtx | null>(null);

export function useTune() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useTune must be used within <TuneProvider>");
  return c;
}

export function TuneProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const targetRef = useRef<HTMLDivElement>(null);
  const [target, setTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setTarget(targetRef.current);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <Ctx.Provider value={{ open, setOpen, target }}>
      {children}
      <div
        onClick={() => setOpen(false)}
        aria-hidden
        className={`fixed inset-0 z-40 bg-ink/30 backdrop-blur-[1px] transition-opacity duration-300 ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
      <aside
        role="dialog"
        aria-label="Tilpas visning"
        aria-hidden={!open}
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-[380px] bg-paper border-l border-rule shadow-2xl transition-transform duration-300 ease-out flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-rule px-6 py-4">
          <p className="eyebrow">Tilpas</p>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Luk panel"
            className="text-ink-soft hover:text-bordeaux transition-colors"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <section className="pb-5 border-b border-rule">
            <p className="eyebrow mb-3">Globalt</p>
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <span className="inline-flex items-center gap-2 meta">
                <Lock size={12} strokeWidth={1.5} className="text-ink-soft" />
                Skjul artikler bag betalingsmur
              </span>
              <PaywallToggle showLabel={false} />
            </label>
          </section>

          <div
            ref={targetRef}
            className="pt-5 space-y-6 empty:pt-0 [&:empty+p]:block"
          />
          <p className="hidden meta italic pt-6 text-ink-soft text-center">
            Flere valg dukker op, når siden har filtre eller søgning.
          </p>
        </div>
      </aside>
    </Ctx.Provider>
  );
}

export function TuneTrigger({
  className = "",
  label = "Tilpas",
}: {
  className?: string;
  label?: string;
}) {
  const { setOpen } = useTune();
  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label="Åbn tilpasningspanel"
      className={`eyebrow inline-flex items-center gap-1.5 text-ink-soft hover:text-bordeaux transition-colors shrink-0 ${className}`}
    >
      <SlidersHorizontal size={14} strokeWidth={1.5} />
      {label}
    </button>
  );
}

/**
 * Portal children into the global Tune panel. Use inside a page component to
 * expose that page's search, tabs and facet controls in the shared offcanvas.
 * Renders nothing inline.
 */
export function PageTuneSlot({ children }: { children: ReactNode }) {
  const { target } = useTune();
  if (!target) return null;
  return createPortal(children, target);
}
